import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

const INTERVAL = 3500;

export function Gallery() {
  const { t, tl } = useLang();
  const { data: allImages = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await api.get("/gallery")).data,
  });
  const images = allImages.slice(0, 15);

  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const pausedRef = useRef(false);
  const count = images.length;

  useEffect(() => { if (index >= count && count > 0) setIndex(0); }, [count]); // eslint-disable-line

  const go = (d) => { setDir(d); setIndex((i) => (i + d + count) % count); };
  const goTo = (i) => { setDir(i > index ? 1 : -1); setIndex(i); };

  useEffect(() => {
    if (count <= 1) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => { if (!pausedRef.current) setIndex((i) => (i + 1) % count); }, INTERVAL);
    return () => clearInterval(id);
  }, [count]);

  const current = count > 0 ? images[Math.min(index, count - 1)] : null;

  return (
    <section id="gallery" data-testid="gallery-section" className="relative z-10 py-12 sm:py-16 bg-[#0F172A] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="gallery.eyebrow" as="span" /></span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight"><EditableText k="gallery.title" as="span" /></h2>
          <p className="mt-4 max-w-md text-white/70"><EditableText k="gallery.desc" as="span" multiline /></p>
        </Reveal>

        {current && (
          <Reveal>
            <div
              data-testid="gallery-slider"
              className="mt-8 relative overflow-hidden rounded-3xl bg-black/30 ring-1 ring-white/10"
              onMouseEnter={() => { pausedRef.current = true; }}
              onMouseLeave={() => { pausedRef.current = false; }}
            >
              <div className="relative aspect-[16/9] sm:aspect-[21/9]">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={resolveImg(current.image_url)}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
                    />
                    <img
                      src={resolveImg(current.image_url)}
                      alt={tl(current, "caption")}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </motion.div>
                </AnimatePresence>

                {tl(current, "caption") && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 pt-16">
                    <span data-testid="gallery-caption" className="text-sm sm:text-base font-medium">{tl(current, "caption")}</span>
                  </div>
                )}

                {count > 1 && (
                  <>
                    <button
                      type="button" data-testid="gallery-prev" aria-label="Previous" onClick={() => go(-1)}
                      className="absolute start-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-black/40 hover:bg-[#BE923C] backdrop-blur transition-colors"
                    >
                      <ChevronLeft size={22} className="rtl:hidden" />
                      <ChevronRight size={22} className="hidden rtl:block" />
                    </button>
                    <button
                      type="button" data-testid="gallery-next" aria-label="Next" onClick={() => go(1)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-black/40 hover:bg-[#BE923C] backdrop-blur transition-colors"
                    >
                      <ChevronRight size={22} className="rtl:hidden" />
                      <ChevronLeft size={22} className="hidden rtl:block" />
                    </button>
                  </>
                )}
              </div>

              {count > 1 && (
                <div data-testid="gallery-dots" className="flex flex-wrap items-center justify-center gap-2 py-4">
                  {images.map((img, i) => (
                    <button
                      key={img.id} type="button" aria-label={`Go to slide ${i + 1}`}
                      data-testid={`gallery-dot-${i}`} onClick={() => goTo(i)}
                      className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-[#BE923C]" : "w-2 bg-white/30 hover:bg-white/50"}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
